import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {SavingState} from 'constants/RegistryConstants';
import navigateToURL from 'lib/navigateToURL';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {CreateLocationSchemaParams, UpdateLocationSchemaParams} from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {LocationSchema} from 'samples/storables/StorableSchemaConstants';
import SchemaSettingsPageContainer, {ArchiveSchemaConfig} from 'schemas/SchemaSettingsPageContainer';
import StorableSchemaValidationTab from 'settings/samples/StorableSchemaValidationTab';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';

import LocationSchemaSettings from './LocationSchemaSettings';

class LocationSchemaSettingsPageConnector extends DataConnector<LocationSchemaSettingsProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [SampleSchemaStore];

  enhanceProps(): EnhancedProps {
    const newState = {
      savingState: this.sampleSchemaStore().getSavingState(),
    };
    if (this.props.locationSchemaId) {
      Object.assign(newState, {
        locationSchema: this.sampleSchemaStore().getLocationSchema(this.props.locationSchemaId),
      });
    }
    return newState;
  }
}

export interface LocationSchemaSettingsProps {
  locationSchemaId?: string;
  ownerUrlId: string;
  registryId: string;
  activeTab: LocationSchemaSettingsTab;
}

export interface EnhancedProps {
  locationSchema?: LocationSchema;
  savingState: SavingState;
}

interface QueryingProps {
  isWritable: boolean;
}

export enum LocationSchemaSettingsTab {
  SCHEMA = 'SCHEMA',
  VALIDATION = 'VALIDATION',
}

function routeSectionForSettingsTab(tab: LocationSchemaSettingsTab): string {
  const settingsTabToRouteSection = {
    [LocationSchemaSettingsTab.SCHEMA]: '',
    [LocationSchemaSettingsTab.VALIDATION]: '/validation',
  };
  return settingsTabToRouteSection[tab];
}

export function locationSettingsTabForRouteSection(routeSection?: string | null): LocationSchemaSettingsTab {
  if (routeSection === 'validation') {
    return LocationSchemaSettingsTab.VALIDATION;
  }
  return LocationSchemaSettingsTab.SCHEMA;
}

type CombinedProps = LocationSchemaSettingsProps & EnhancedProps & QueryingProps;

export class LocationSchemaSettingsPage extends React.Component<CombinedProps> {
  override componentDidMount(): void {
    this.fetchLocationSchemas();
  }

  override componentDidUpdate(prevProps: CombinedProps): void {
    if (
      prevProps.savingState === SavingState.PENDING &&
      RegistryUtil.isSavingFinished(this.props.savingState)
    ) {
      SampleSchemaActions.resetSavingState();
    }
  }

  private fetchLocationSchemas = (): void => {
    SampleSchemaActions.fetchAllLocationSchemas();
  };

  private handleNavigateToLocationSchemas = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getRegistryLocationSchemasUrl(this.props.ownerUrlId, this.props.registryId)
    );
  };

  private getArchiveConfig = (): ArchiveSchemaConfig | undefined => {
    if (!this.props.locationSchema || !this.props.isWritable) {
      return undefined;
    }

    return {
      onArchive: (id: string, purpose: string): void => {
        ArchiveActions.bulkArchiveStorableSchemas([id], purpose);
      },
      onUnarchive: (id: string): void => {
        ArchiveActions.bulkUnarchiveStorableSchemas([id]);
      },
      isArchivable: true,
      isUnarchivable: true,
    };
  };

  private handleCreate = (locationSchemaCreateParams: CreateLocationSchemaParams): void => {
    SampleSchemaActions.createLocationSchema(
      this.props.registryId,
      this.props.ownerUrlId,
      locationSchemaCreateParams
    );
  };

  private handleUpdate = (locationSchemaUpdates: UpdateLocationSchemaParams): void => {
    assert(this.props.locationSchemaId != null, 'locationSchemaId must exist for update.');
    if (this.props.locationSchemaId) {
      SampleSchemaActions.updateLocationSchema(this.props.locationSchemaId, locationSchemaUpdates);
    }
  };

  private navigateToUrlForSettingsTab = (tab: LocationSchemaSettingsTab): void => {
    if (!this.props.locationSchemaId) {
      return;
    }
    const routeSegmentForTab = routeSectionForSettingsTab(tab);
    const locationSchemaSettingsUrl = RegistryUtil.getLocationSchemaSettingsUrl(
      this.props.ownerUrlId,
      this.props.registryId,
      this.props.locationSchemaId
    );
    navigateToURL(`${locationSchemaSettingsUrl}${routeSegmentForTab}`);
  };

  private shouldRenderValidationSettingsTab(): boolean {
    return this.props.locationSchema != null;
  }

  private shouldRenderTabs = (): boolean =>
    this.props.locationSchema != null && this.shouldRenderValidationSettingsTab();

  private getSanitizedActiveTab(): LocationSchemaSettingsTab {
    if (
      this.props.activeTab === LocationSchemaSettingsTab.VALIDATION &&
      !this.shouldRenderValidationSettingsTab()
    ) {
      return LocationSchemaSettingsTab.SCHEMA;
    }
    return this.props.activeTab;
  }

  override render(): JSX.Element {
    if (this.props.locationSchema) {
      BenchlingUtil.setWindowTitle(`${this.props.locationSchema.name} - Location Schemas`);
    }

    const locationSchemaContents = (
      <LocationSchemaSettings
        isBeingCreated={!this.props.locationSchemaId}
        isWritable={this.props.isWritable}
        locationSchema={this.props.locationSchema}
        registryId={this.props.registryId}
        ownerUrlId={this.props.ownerUrlId}
        onCreate={this.handleCreate}
        onUpdate={this.handleUpdate}
        savingState={this.props.savingState}
      />
    );

    return (
      <SchemaSettingsPageContainer
        archiveConfig={this.getArchiveConfig()}
        onNavigateToListing={this.handleNavigateToLocationSchemas}
        schema={this.props.locationSchemaId && this.props.locationSchema ? this.props.locationSchema : null}
        schemaTypeName="Location Schema"
        showExport={licenses.hasEnterprisePlan()}
      >
        {this.props.locationSchemaId && !this.props.locationSchema ? (
          <HorizontalThrobber />
        ) : this.shouldRenderTabs() ? (
          <TabContainer
            activeTab={this.getSanitizedActiveTab()}
            className="u-flex-grow"
            onRequestTabChange={this.navigateToUrlForSettingsTab}
          >
            <Tab tabKey={LocationSchemaSettingsTab.SCHEMA} title="Location schema settings">
              {locationSchemaContents}
            </Tab>
            {this.shouldRenderValidationSettingsTab() && (
              <Tab tabKey={LocationSchemaSettingsTab.VALIDATION} title="Validation failures">
                <div>
                  <StorableSchemaValidationTab
                    storableSchemaId={this.props.locationSchemaId!}
                    registryId={this.props.registryId}
                  />
                </div>
              </Tab>
            )}
          </TabContainer>
        ) : (
          locationSchemaContents
        )}
      </SchemaSettingsPageContainer>
    );
  }
}

function QueryingLocationSchemaSettingsPage(props: LocationSchemaSettingsProps & EnhancedProps): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  return <LocationSchemaSettingsPage {...props} isWritable={isWritable} />;
}

export default LocationSchemaSettingsPageConnector.connect(QueryingLocationSchemaSettingsPage);
